import { LockSettingType, NameViewMode, Show } from "../../../constants/schema.js";
import { arrayHas } from "../../../utils/arrayHas.js";
import { DictionaryKind } from "../dictionary/actions.js";
import { addColumnAction, changeColumnCommentAction, changeColumnDataTypeAction, changeColumnDefaultAction, changeColumnDomainAction, changeColumnLogicalNameAction, changeColumnNameAction, changeColumnNotNullAction, changeColumnPrimaryKeyAction, moveColumnAction, removeColumnAction } from "../table-column/atom.actions.js";
import { addDictionaryAction, removeDictionaryAction, updateDictionaryAction } from "../dictionary/atom.actions.js";
import { Tag } from "../../tag.js";
import { bHas } from "../../../utils/bit.js";
import { ViewKind } from "../editor/state.js";
import { getActiveView, isViewShown } from "../editor/view.js";
import { viewChangeShowModeAction, viewChangeZoomLevelAction, viewCloseAction, viewMoveTableAction, viewOpenAction, viewScrollToAction, viewSetCentersAction, viewSetLayoutAction, viewStepCentersAction, viewStreamScrollToAction, viewStreamZoomLevelAction } from "../editor/view.actions.js";
import { getVisibleIds } from "../../../konva/scene/viewLayout.js";
import { invalidateTableWidths } from "../../../utils/calcTable.js";
import { changeLockSettingsAction, changeMaxWidthCommentAction, changeNameViewModeAction, changeShowAction } from "../settings/atom.actions.js";
import { recalculateIdentification, recalculateStartRelationshipType } from "./endFlags.js";
import { relationshipSort } from "../../../utils/draw-relationship/sort.js";
import { addIndexAction, changeIndexUniqueAction, removeIndexAction } from "../index/atom.actions.js";
import { addIndexColumnAction, moveIndexColumnAction, removeIndexColumnAction } from "../index-column/atom.actions.js";
import { moveMemoAction } from "../memo/atom.actions.js";
import { addRelationshipAction, changeRelationshipColumnsAction, removeRelationshipAction } from "./atom.actions.js";
import { addTableAction, changeTableCommentAction, changeTableLogicalNameAction, changeTableNameAction, moveTableAction, moveToTableAction, removeTableAction, sortTableAction } from "../table/atom.actions.js";
import { isNil, isString } from "es-toolkit";
import { asapScheduler, filter, observeOn, tap, throttle, timer } from "rxjs";
//#region src/engine/modules/relationship/hooks.ts
var identificationHook = (action$, getState) => action$.pipe(throttle(() => timer(0, asapScheduler), {
	leading: false,
	trailing: true
})).subscribe(() => recalculateIdentification(getState()));
var startRelationshipHook = (action$, getState) => action$.pipe(throttle(() => timer(0, asapScheduler), {
	leading: false,
	trailing: true
}), observeOn(asapScheduler)).subscribe(() => recalculateStartRelationshipType(getState()));
var isMoveOnly = arrayHas([
	moveTableAction.type,
	moveToTableAction.type,
	moveMemoAction.type
]);
var sortWindow = ({ tags }) => !isNil(tags) && bHas(tags, Tag.drag) ? timer(5) : timer(0, asapScheduler);
var alternateKeyActions = [
	addIndexAction,
	removeIndexAction,
	changeIndexUniqueAction,
	addIndexColumnAction,
	removeIndexColumnAction,
	moveIndexColumnAction,
	moveColumnAction
];
var alternateKeyActionTypes = alternateKeyActions.map((action) => action.type);
var isAlternateKeyAction = arrayHas(alternateKeyActionTypes);
var isPaintOnlyShow = arrayHas([
	Show.hideReferentialAction,
	Show.hideTableGroup,
	Show.hideMemo
]);
var isPaintOnlyToggle = (action) => action.type === changeShowAction.type && isPaintOnlyShow(action.payload?.show);
var isDictionaryWrite = arrayHas([
	addDictionaryAction.type,
	updateDictionaryAction.type,
	removeDictionaryAction.type
]);
var reachesLayout = ({ type, payload }, state) => {
	if (type === changeNameViewModeAction.type) return payload.shared === true || payload.editorId === state.editor.id;
	if (isDictionaryWrite(type)) return payload?.kind === DictionaryKind.domain && (type !== updateDictionaryAction.type || isString(payload.values?.name));
	if (type === changeLockSettingsAction.type) return payload?.value === false && bHas(payload.lockSettingType, LockSettingType.unlockNameViewMode);
	return true;
};
var relationshipSortHook = (action$, getState, ctx) => action$.pipe(filter((action) => (!isAlternateKeyAction(action.type) || bHas(getState().settings.show, Show.columnAlternateKey)) && !isPaintOnlyToggle(action) && reachesLayout(action, getState())), tap((action) => {
	if (!isMoveOnly(action.type)) invalidateTableWidths();
}), throttle(sortWindow, {
	leading: false,
	trailing: true
})).subscribe(() => {
	relationshipSort(getState(), "document", { route: ctx.routes !== false });
});
var isDocumentOnly = arrayHas([
	changeShowAction.type,
	changeMaxWidthCommentAction.type,
	moveMemoAction.type,
	...alternateKeyActionTypes
]);
var isNameViewOnly = arrayHas([
	changeTableLogicalNameAction.type,
	changeColumnLogicalNameAction.type,
	changeColumnDomainAction.type,
	addDictionaryAction.type,
	updateDictionaryAction.type,
	removeDictionaryAction.type
]);
var isViewTransform = arrayHas([
	viewScrollToAction.type,
	viewStreamScrollToAction.type,
	viewChangeZoomLevelAction.type,
	viewStreamZoomLevelAction.type
]);
var idOf = ({ id }) => [id];
var idsOf = ({ ids }) => ids;
var tableIdOf = ({ tableId }) => [tableId];
var kindOf = (_, { kind }) => kind;
var flowOnly = () => ViewKind.flow;
var namedOrActiveKind = (state, { kind }) => kind ?? getActiveView(state)?.kind ?? null;
var aimedKind = {
	[viewOpenAction.type]: kindOf,
	[viewCloseAction.type]: kindOf,
	[viewSetLayoutAction.type]: kindOf,
	[viewSetCentersAction.type]: flowOnly,
	[viewStepCentersAction.type]: flowOnly,
	[viewChangeShowModeAction.type]: namedOrActiveKind,
	[viewMoveTableAction.type]: namedOrActiveKind
};
var namedIds = {
	[addTableAction.type]: idOf,
	[removeTableAction.type]: idOf,
	[moveToTableAction.type]: idOf,
	[changeTableNameAction.type]: idOf,
	[changeTableLogicalNameAction.type]: idOf,
	[changeTableCommentAction.type]: idOf,
	[addRelationshipAction.type]: idOf,
	[removeRelationshipAction.type]: idOf,
	[changeRelationshipColumnsAction.type]: ({ id, start, end }) => [
		id,
		start.tableId,
		end.tableId
	],
	[moveTableAction.type]: idsOf,
	[viewMoveTableAction.type]: idsOf,
	[addColumnAction.type]: tableIdOf,
	[removeColumnAction.type]: tableIdOf,
	[changeColumnNameAction.type]: tableIdOf,
	[changeColumnLogicalNameAction.type]: tableIdOf,
	[changeColumnDomainAction.type]: tableIdOf,
	[changeColumnCommentAction.type]: tableIdOf,
	[changeColumnDataTypeAction.type]: tableIdOf,
	[changeColumnDefaultAction.type]: tableIdOf,
	[changeColumnPrimaryKeyAction.type]: tableIdOf
};
function shownIds(state, source) {
	const { tableIds, relationshipIds } = getVisibleIds(state, source);
	return /* @__PURE__ */ new Set([...tableIds, ...relationshipIds]);
}
function touchesView(state, action, lastRead, source) {
	if (isDocumentOnly(action.type) || isViewTransform(action.type)) return false;
	if (isNameViewOnly(action.type) && state.settings.nameViewMode === NameViewMode.physical) return false;
	const aimed = aimedKind[action.type];
	if (aimed && aimed(state, action.payload) !== source) return false;
	const named = namedIds[action.type];
	if (!named) return true;
	const shown = shownIds(state, source);
	return named(action.payload).some((id) => shown.has(id) || lastRead.has(id));
}
var VIEW_SOURCES = [ViewKind.flow];
var viewRelationshipSortHook = (action$, getState) => {
	const lastRead = { flow: /* @__PURE__ */ new Set() };
	const pending = /* @__PURE__ */ new Set();
	return action$.pipe(filter((action) => {
		const state = getState();
		if (!reachesLayout(action, state)) return false;
		let touched = false;
		for (const source of VIEW_SOURCES) if (isViewShown(state, source) && touchesView(state, action, lastRead[source], source)) {
			pending.add(source);
			touched = true;
		}
		return touched;
	}), throttle(sortWindow, {
		leading: false,
		trailing: true
	})).subscribe(() => {
		const state = getState();
		const sources = [...pending];
		pending.clear();
		for (const source of sources) {
			if (!isViewShown(state, source)) continue;
			relationshipSort(state, source);
			lastRead[source] = shownIds(state, source);
		}
	});
};
var layoutActions = [
	changeShowAction,
	changeMaxWidthCommentAction,
	addRelationshipAction,
	removeRelationshipAction,
	changeRelationshipColumnsAction,
	moveMemoAction,
	addTableAction,
	removeTableAction,
	moveTableAction,
	moveToTableAction,
	changeTableNameAction,
	changeTableCommentAction,
	addColumnAction,
	removeColumnAction,
	changeColumnNameAction,
	changeColumnCommentAction,
	changeColumnDataTypeAction,
	changeColumnDefaultAction,
	sortTableAction,
	...alternateKeyActions,
	changeNameViewModeAction,
	changeLockSettingsAction,
	changeTableLogicalNameAction,
	changeColumnLogicalNameAction,
	changeColumnDomainAction,
	addDictionaryAction,
	updateDictionaryAction,
	removeDictionaryAction
];
var viewRowActions = [changeColumnPrimaryKeyAction];
var viewLayoutActions = [
	viewOpenAction,
	viewCloseAction,
	viewScrollToAction,
	viewStreamScrollToAction,
	viewChangeZoomLevelAction,
	viewStreamZoomLevelAction,
	viewMoveTableAction,
	viewSetLayoutAction,
	viewChangeShowModeAction,
	viewSetCentersAction,
	viewStepCentersAction
];
var hooks = [
	[[
		addColumnAction,
		removeColumnAction,
		changeColumnPrimaryKeyAction,
		addRelationshipAction,
		removeRelationshipAction,
		changeRelationshipColumnsAction,
		addTableAction,
		removeTableAction
	], identificationHook],
	[[
		addColumnAction,
		removeColumnAction,
		changeColumnNotNullAction,
		changeColumnPrimaryKeyAction,
		addRelationshipAction,
		removeRelationshipAction,
		changeRelationshipColumnsAction,
		addTableAction,
		removeTableAction
	], startRelationshipHook],
	[layoutActions, relationshipSortHook],
	[[
		...layoutActions,
		...viewRowActions,
		...viewLayoutActions
	], viewRelationshipSortHook]
];
//#endregion
export { hooks };
